import { useCallback, useMemo } from "react";

import { useNavigationHelpers } from "@/lib/navigation-helpers";
import { MenuHeader, SubMenu } from "@/types/menu";
import { APROVACAO_MENU_IDS } from "@/components/menu/menu-catalog";

const APROVACOES_URL = "/aprovacoes";

export type MenuNavTarget = MenuHeader | SubMenu | string;

interface UseMenuNavigationOptions {
  /**
   * Disparado logo após navegar para Aprovações. O caller decide o efeito
   * colateral (ex.: agendar o refresh do contador de notificações), pois é
   * ele quem é dono da fonte do badge.
   */
  onAprovacaoNavigate?: () => void;
}

/**
 * Resolução única de navegação para *folhas* de menu (itens sem submenu).
 *
 * Centraliza a tabela de ações de rota que antes estava duplicada entre a
 * Sidebar e a Menu (hub). Cobre remaps reais (ex.: a url de menu `/permissoes`
 * aponta para a rota `/admin/permissoes`) e helpers que resetam estado de
 * listagens; urls não mapeadas caem no `goTo` padrão.
 *
 * A decisão "expandir inline (sidebar)" x "drill-in `/modulo/:id` (hub)" NÃO
 * mora aqui — é responsabilidade de cada view, pois é comportamento de
 * apresentação, não de navegação de destino.
 */
export function useMenuNavigation(options: UseMenuNavigationOptions = {}) {
  const helpers = useNavigationHelpers();
  const { onAprovacaoNavigate } = options;

  const routeActions = useMemo<Record<string, () => void>>(
    () => ({
      "/job": helpers.goToJobList,
      "/planilhas/atendimento": helpers.goToAtendimentoList,
      "/planilhas/pre-producao": helpers.goToPreProducaoList,
      "/planilhas/compras": helpers.goToCompraList,
      "/financeiro/faturamentos": helpers.goToFaturamentoFinanceiroList,
      "/producao-financeiro/aps": helpers.goToProducaoFinanceiroAps,
      "/lotes": helpers.goToLoteList,
      "/perfil": helpers.goToPessoaPerfilPage,
      "/permissoes": helpers.goToPermissoesPage,
    }),
    [helpers],
  );

  const navigateToMenuLeaf = useCallback(
    (target: MenuNavTarget) => {
      const url = typeof target === "string" ? target : target.url || "/";
      const id = typeof target === "string" ? undefined : target.id;

      const isAprovacoes =
        url === APROVACOES_URL || (id !== undefined && APROVACAO_MENU_IDS.includes(id));
      if (isAprovacoes) {
        helpers.goToAprovacaoList();
        onAprovacaoNavigate?.();
        return;
      }

      const action = routeActions[url];
      if (action) action();
      else helpers.goTo(url);
    },
    [helpers, routeActions, onAprovacaoNavigate],
  );

  return { navigateToMenuLeaf };
}
